import { useState } from "react";
import styles from "./ReportModal.module.css";

type ReportType = "spam" | "hate" | "violent" | "other";

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reportType: ReportType, details?: string) => Promise<void>;
}

const REPORT_OPTIONS: { value: ReportType; label: string; description: string }[] = [
  {
    value: "spam",
    label: "Spam",
    description: "Repetitive, promotional, or nonsensical content",
  },
  {
    value: "hate",
    label: "Hate Speech",
    description: "Discriminatory or hateful language targeting groups",
  },
  {
    value: "violent",
    label: "Violence",
    description: "Threats, glorification of violence, or harmful content",
  },
  {
    value: "other",
    label: "Other",
    description: "Something else that violates community guidelines",
  },
];

export function ReportModal({ isOpen, onClose, onSubmit }: ReportModalProps) {
  const [selectedType, setSelectedType] = useState<ReportType | null>(null);
  const [details, setDetails] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedType || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(selectedType, details || undefined);
      setSubmitted(true);
      // Auto close after success
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      console.error("Failed to submit report:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    setDetails("");
    setSubmitted(false);
    onClose();
  };

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={handleClose}>
          ×
        </button>

        {submitted ? (
          <div className={styles.success}>
            <div className={styles.successIcon}>✓</div>
            <h3>Report Submitted</h3>
            <p>Thank you for helping keep our community safe.</p>
          </div>
        ) : (
          <>
            <h2 className={styles.title}>Report Answer</h2>
            <p className={styles.subtitle}>
              Why are you reporting this answer?
            </p>

            <div className={styles.options}>
              {REPORT_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`${styles.option} ${
                    selectedType === option.value ? styles.selected : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="reportType"
                    value={option.value}
                    checked={selectedType === option.value}
                    onChange={() => setSelectedType(option.value)}
                  />
                  <div className={styles.optionContent}>
                    <span className={styles.optionLabel}>{option.label}</span>
                    <span className={styles.optionDesc}>{option.description}</span>
                  </div>
                </label>
              ))}
            </div>

            {selectedType === "other" && (
              <textarea
                className={styles.details}
                placeholder="Please provide more details..."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                maxLength={500}
              />
            )}

            <div className={styles.actions}>
              <button className={styles.cancelBtn} onClick={handleClose}>
                Cancel
              </button>
              <button
                className={styles.submitBtn}
                onClick={handleSubmit}
                disabled={!selectedType || isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Report"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

